import type { PropsWithChildren } from 'react'
import { createContext, useContext } from 'react'
import type { SystemProps } from '@xstyled/styled-components'

type CommonTextStyles = {
  fontSize: SystemProps['fontSize']
  lineHeight: SystemProps['lineHeight']
}

export type ToteCardSize = 'sm' | 'md'

export type ToteConfig = {
  size?: ToteCardSize
  spacing: number
  infoButtonSize: number
  textStyles: CommonTextStyles
  dateTextStyles: CommonTextStyles
  actionTextStyles: CommonTextStyles & {
    fontWeight: SystemProps['fontWeight']
  }
}

export interface ToteCardContextValues {
  config: ToteConfig
}

export const ToteCardContext = createContext<ToteCardContextValues | null>(null)

export interface ToteCardProviderProps {
  size?: ToteCardSize
}

export const ToteCardProvider = (
  props: PropsWithChildren<ToteCardProviderProps>
): JSX.Element => {
  const size = props.size || 'md'

  const commonTextStyles: CommonTextStyles = {
    fontSize: size,
    lineHeight: size
  }

  const config: ToteConfig = {
    size,
    spacing: size === 'md' ? 16 : 8,
    textStyles: commonTextStyles,
    dateTextStyles: {
      fontSize: size === 'md' ? 'sm' : 'xs',
      lineHeight: size === 'md' ? 'sm' : 'xs'
    },
    actionTextStyles: {
      ...commonTextStyles,
      fontWeight: size === 'md' ? 700 : 500
    },
    infoButtonSize: size === 'md' ? 20 : 16
  }

  return (
    <ToteCardContext.Provider value={{ config }}>
      {props.children}
    </ToteCardContext.Provider>
  )
}

export const useToteCardContext = (): ToteCardContextValues => {
  const context = useContext(ToteCardContext)
  if (!context) {
    throw new Error('ToteCardContext is not available')
  }
  return context
}
